<script setup lang="ts" generic="T">
import type {
  ISelectSectionSimpleTreeExpose,
  ISelectSectionSimpleTreeNodeTag,
} from "#/ui-kit/components/sections/select-section-simple-tree.types";
import type {
  IVirtualizedTreeExpose,
  IVirtualizedTreeNode,
  IVirtualizedTreeNodeModel,
} from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree.types";

import VirtualizedTree from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree.vue";
import { CubeSmallIcon, MarkerPinSmallIcon } from "#/ui-kit/icons-api";
import { DATA_TEST_IDS } from "@voltalis/frontend-utils";
import { useElementSize } from "@vueuse/core";
import { ref, useTemplateRef, watch } from "vue";

import RadioButton from "primevue/radiobutton";

export interface ISelectSectionSimpleTreeProps<T> {
  siteSectionsTree: IVirtualizedTreeNode<T | null>;
  initialSelectedSection: T | null;
  showRadioButtonCb?: (node: IVirtualizedTreeNode<T | null>) => boolean;
  isNodeDisabled?: (node: IVirtualizedTreeNode<T | null>) => boolean;
  getNodeTagsCb?: (node: IVirtualizedTreeNode<T | null>) => ISelectSectionSimpleTreeNodeTag[];
  emitInitialSelection?: boolean;
}

export interface ISelectSectionSimpleTreeEmits<T> {
  (e: "selection", data: T | null): void;
}

const props = withDefaults(defineProps<ISelectSectionSimpleTreeProps<T>>(), {
  showRadioButtonCb: () => true,
  isNodeDisabled: () => false,
  getNodeTagsCb: () => [],
  emitInitialSelection: true,
});
const emits = defineEmits<ISelectSectionSimpleTreeEmits<T>>();

const NODE_HEIGHT = 44;
const ROOT_VALUE = "root";

const container = useTemplateRef("container");
const { height: containerHeight } = useElementSize(container);

const selectedNode = ref<T | typeof ROOT_VALUE>(props.initialSelectedSection ?? ROOT_VALUE);
const virtualizedTree = useTemplateRef<IVirtualizedTreeExpose<IVirtualizedTreeNodeModel<T | null>>>("virtualizedTree");

async function forceUpdate(): Promise<void> {
  await virtualizedTree.value?.forceUpdate();
}

function scrollToNode(findNodeFn: (node: IVirtualizedTreeNodeModel<T | null>) => boolean): void {
  virtualizedTree.value?.scrollToNode(findNodeFn);
}

function shouldShowRadioButton(node: IVirtualizedTreeNode<T | null>): boolean {
  return props.showRadioButtonCb?.(node) ?? true;
}

function isTreeNodeDisabled(node: IVirtualizedTreeNode<T | null>): boolean {
  return props.isNodeDisabled?.(node) ?? false;
}

function getNodeTags(node: IVirtualizedTreeNode<T | null>): ISelectSectionSimpleTreeNodeTag[] {
  return props.getNodeTagsCb?.(node) ?? [];
}

watch(
  selectedNode,
  (newValue) => {
    if (newValue === ROOT_VALUE) {
      emits("selection", null);
      return;
    }

    emits("selection", newValue);
  },
  { immediate: props.emitInitialSelection },
);

defineExpose<ISelectSectionSimpleTreeExpose<T>>({
  forceUpdate,
  scrollToNode,
});
</script>

<template>
  <div
    ref="container"
    class="w-full h-full border rounded-lg"
    :data-test-id="DATA_TEST_IDS.components.selectSectionSimpleTree.component"
  >
    <Suspense>
      <template #fallback>
        <slot name="loader" />
      </template>
      <VirtualizedTree
        class="select-section-tree"
        ref="virtualizedTree"
        :nodes="[props.siteSectionsTree]"
        :itemHeight="NODE_HEIGHT"
        :viewportHeight="containerHeight"
      >
        <template #padding>
          <div class="tree-node-padding" />
        </template>
        <template #item="{ item: section, handleToggleNode }">
          <div
            class="node flex items-center px-4 justify-between w-full gap-2 h-full"
            :class="{
              disabled: isTreeNodeDisabled(section),
            }"
            :data-test-id="DATA_TEST_IDS.components.selectSectionSimpleTree.sectionItem"
          >
            <i
              :class="[
                'pi ',
                section.state.isExpanded ? 'pi-angle-down' : 'pi-angle-right',
                section.children.length ? 'with-children' : 'without-children',
              ]"
              class="text-xs text-gray-500"
              @click="handleToggleNode(section)"
            />

            <MarkerPinSmallIcon v-if="section.parentsIndexes.length === 0" />
            <CubeSmallIcon v-else />

            <div class="flex w-full justify-between items-center">
              <span
                class="flex min-w-0 items-center gap-2 font-medium text-gray-700"
                :data-test-id="DATA_TEST_IDS.components.selectSectionSimpleTree.sectionName"
              >
                <span class="overflow-hidden text-ellipsis whitespace-nowrap">{{ section.name }}</span>
                <span
                  v-for="tag in getNodeTags(section)"
                  :key="`${section.key}-${tag.variant}`"
                  class="select-section-tree-badge"
                  :class="`select-section-tree-badge--${tag.variant}`"
                >
                  {{ tag.label }}
                </span>
              </span>

              <div v-if="shouldShowRadioButton(section) && !isNodeDisabled(section)">
                <RadioButton
                  v-if="section.data === null"
                  v-model="selectedNode"
                  :value="ROOT_VALUE"
                  :pt="{
                    root: {
                      'data-test-id': DATA_TEST_IDS.components.selectSectionSimpleTree.sectionRootRadioButton,
                    },
                  }"
                />
                <RadioButton
                  v-else
                  v-model="selectedNode"
                  :value="section.data"
                  :pt="{
                    root: {
                      'data-test-id': DATA_TEST_IDS.components.selectSectionSimpleTree.sectionRadioButton,
                    },
                  }"
                />
              </div>
            </div>
          </div>
        </template>
      </VirtualizedTree>
    </Suspense>
  </div>
</template>

<style lang="scss">
.select-section-tree {
  ::deep(.virtualized-tree-item-container) {
    &:hover {
      // bg-blue-100/20
      background-color: rgb(219 234 254 / 20%);
    }
  }
}
</style>

<style scoped lang="scss">
.select-section-tree-badge {
  @apply inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium leading-4;
}

.select-section-tree-badge--parent {
  @apply bg-gray-100 text-gray-600;
}

.select-section-tree-badge--destination {
  @apply bg-blue-100 text-blue-700;
}

.tree-node-padding {
  @apply h-full;
  margin-left: 2rem;
}

.with-children {
  cursor: pointer;
}

.without-children {
  display: none;
}

.node.disabled {
  @apply opacity-50;
}
</style>
